import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, ShieldX, Clock } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Logo } from "@/components/Logo";
import { Loader } from "@/components/Loader";
import { STATUS_META } from "@/lib/brand";

export default function Verify() {
  const { code } = useParams();
  const { t, lang } = useApp();
  const [data, setData] = useState(undefined);

  useEffect(() => {
    api.get(`/verify/${encodeURIComponent(code)}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ found: false }));
  }, [code]);

  if (data === undefined) return <div className="min-h-screen flex items-center justify-center"><Loader /></div>;

  const found = data.found;
  const valid = data.valid;
  const meta = STATUS_META[data.status] || { en: data.status, it: data.status, color: "#A39E93" };
  const statusLabel = found ? (meta[lang] || meta.en) : "";
  const fmt = (d) => d ? new Date(d).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { day: "2-digit", month: "long", year: "numeric" }) : "—";

  const Banner = () => {
    if (!found) return (
      <div className="flex flex-col items-center gap-3" data-testid="verify-notfound">
        <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ border: "1px solid #A39E9355", background: "#A39E9312" }}><ShieldX size={28} className="text-[color:var(--cu-muted)]" /></div>
        <p className="font-serif-cu text-xl">{t("verify_not_found")}</p>
      </div>
    );
    if (valid) return (
      <div className="flex flex-col items-center gap-3" data-testid="verify-valid">
        <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ border: "1px solid #10B98155", background: "#10B98112" }}><ShieldCheck size={30} className="text-emerald-400" /></div>
        <p className="font-serif-cu text-2xl text-emerald-400">{t("verify_valid")}</p>
      </div>
    );
    const Icon = data.status === "expired" ? Clock : ShieldX;
    return (
      <div className="flex flex-col items-center gap-3" data-testid="verify-invalid">
        <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ border: `1px solid ${meta.color}55`, background: `${meta.color}12` }}><Icon size={28} style={{ color: meta.color }} /></div>
        <p className="font-serif-cu text-2xl" style={{ color: meta.color }}>{t("verify_not_valid")}</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 pt-8 flex justify-center"><Logo size="sm" /></header>
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-sm fade-up">
          <div className="eyebrow text-center mb-8">{t("verify_title")}</div>
          <div className="cu-card p-8">
            <div className="text-center mb-8"><Banner /></div>
            {found && (
              <div className="space-y-3 text-sm" data-testid="verify-details">
                <Row label={t("member_no")} value={data.membership_number || "—"} mono />
                <Row label={t("status")} value={statusLabel} color={meta.color} />
                <Row label={t("valid_until")} value={fmt(data.valid_until)} />
              </div>
            )}
          </div>
          <p className="text-center text-[0.65rem] text-[color:var(--cu-muted)] mt-6 tracking-[0.12em] font-mono-cu">CAUGHT UP INTERNATIONAL · {t("verify_footer")}</p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono, color }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[color:var(--cu-border)] last:border-0 pb-2.5 last:pb-0">
      <span className="eyebrow">{label}</span>
      <span className={`text-right ${mono ? "font-mono-cu gold-text" : "text-[color:var(--cu-text)]"}`} style={color && !mono ? { color } : {}}>{value}</span>
    </div>
  );
}
